import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider, useSelector, useDispatch } from 'react-redux';
import sanitize from 'sanitize-html';
import parse from 'html-react-parser';
import cN from 'classnames';
import {
    SubTitle,
    ItineraryDaily,
    ItineraryDailyLightbox,
    ItineraryMarketing,
    ItineraryNoticesFrame,
    ItineryTravelInfoM,
    ItineryTravelInfoPC,
    ItinerarySelfGuided,
    Table,
} from '@components/DetailPage';
import FeatureContent from '@components/Common/FeatureContent';
import { RootState, store } from '@Redux/tourdetail/store';
import { I_OptionalInfo, I_StationInfoList } from '@Redux/tourdetail/Itineraries/interfaces';
import {
    setItineraries,
    setOptionalInfo,
    setNoticeinfo,
    setStationInfo,
} from '@Redux/tourdetail/Itineraries/actions';
import { setPageType } from '@Redux/common/Page/actions';
import { ICollapseListProps } from '@lion-design/core';
import 'normalize.css';
import './styles.scss';
import domainByEnv from '@utils/domainByEnv';
import { changeDetailHistory } from '@utils/utils';
import '@magaele/ic-ln/css.css';
interface I_Table {
    title: string;
    content: string;
}

interface I_ItinerarayTitle {
    TourName: string;
    NormGroupID: string;
}

function ItinerarayTitle(props: I_ItinerarayTitle) {
    const [isTitleScrolled, setIsTitleScrolled] = useState(false);

    useEffect(() => {
        function fixTitle() {
            window.scrollY > 110 ? setIsTitleScrolled(true) : setIsTitleScrolled(false);
        }
        window.addEventListener('scroll', fixTitle);

        return function cleanupListener() {
            window.removeEventListener('scroll', fixTitle);
        };
    }, []);

    return (
        <div className={cN('itinerary_title', { fixed: isTitleScrolled })}>
            <h1>{props.TourName}</h1>
            <div className="itinerary_title_anchorWrapper">
                <a href={`${domainByEnv(false)}/detail?NormGroupID=${props.NormGroupID}`}>
                    看出發日及報名
                </a>
            </div>
        </div>
    );
}

function Itineraray() {
    const dispatch = useDispatch();
    const {
        Features,
        OptionalInfoList,
        Remark,
        ItinnotesReg,
        LeaveTeamReg,
        SafeReg,
        ActiveNote,
        EachTripList,
        NoteList,
        DailyList,
        BankContent,
        StationInfoList,
        MarketingContent,
    } = useSelector((store: RootState) => store.itinerariesReducer);

    const { travelinfo } = document.getElementById('detailPagePara')!.dataset!;

    const { TourID, IsPreview, TravelType, GroupID, TourName, NormGroupID } = JSON.parse(
        travelinfo!
    );

    useEffect(() => {
        //現在頁面
        dispatch(setPageType('tourdetail'));
    }, [dispatch]);

    useEffect(() => {
        const historyParams: any = {
            NormGroupID: NormGroupID,
            GroupID: GroupID,
        };

        if (TourID) historyParams.TourID = TourID;

        changeDetailHistory(historyParams);
    }, [GroupID, NormGroupID, TourID]);

    useEffect(() => {
        const itinerariesReqbody = {
            TourID,
            IsPreview,
            TravelType,
        };

        dispatch(setItineraries(itinerariesReqbody));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        const reqBody = {
            GroupID,
        };

        dispatch(setOptionalInfo(reqBody));
        dispatch(setNoticeinfo(reqBody));
        dispatch(setStationInfo(reqBody)); // 集合地點
    }, [GroupID, dispatch]);

    function TableContent(props: I_Table) {
        return (
            <>
                <div className="title">{props.title}</div>
                {props.content ? (
                    <div
                        dangerouslySetInnerHTML={{
                            __html: sanitize(props.content, {
                                allowedTags: false,
                                allowedAttributes: false,
                            }),
                        }}
                    />
                ) : (
                    <div>--</div>
                )}
            </>
        );
    }

    //集合地點
    const placeTitles: ICollapseListProps['titles'] = [
        {
            name: '集合地點',
            key: 'Place',
            size: '33.333%',
        },
        {
            name: '地址',
            key: 'Address',
            size: '33.333%',
        },
        {
            name: '集合時間',
            key: 'MusterTime',
            size: '33.333%',
        },
    ];

    const placeData = StationInfoList.map((StationInfoList: I_StationInfoList) => {
        const { Place, MusterTime, Address } = StationInfoList;
        return {
            Place: <TableContent title="集合地點" content={Place} />,
            MusterTime: <TableContent title="集合時間" content={MusterTime} />,
            Address: <TableContent title="地址" content={Address} />,
        };
    });

    //自費行程
    const activityTitles: ICollapseListProps['titles'] = [
        {
            name: '城市',
            key: 'CityName',
            size: '13%',
        },
        {
            name: '節目名稱',
            key: 'ProgramName',
            size: '13%',
        },
        {
            name: '簡介',
            key: 'Summary',
            size: '18%',
        },
        {
            name: '費用',
            key: 'Cost',
            size: '13%',
        },
        {
            name: '年齡說明',
            key: 'AgeDesc',
            size: '9%',
        },
        {
            name: '備註',
            key: 'Remarks',
            size: '27%',
        },
        {
            name: '危險',
            key: 'DangerLevel',
            size: '7%',
        },
    ];

    const activityData = OptionalInfoList.map((info: I_OptionalInfo) => {
        const Cost = (function getCostHTML() {
            if (info.ChildCost > 0) {
                return ` ${info.CurrName}
                    <div>${`大人$` + info.AdultsCost}</div>
                    <div>${`小孩$` + info.ChildCost}</div>`;
            } else {
                return ` ${info.CurrName}
                    <div>${`大人$` + info.AdultsCost}</div>`;
            }
        })();

        return {
            CityName: <TableContent title="城市" content={info.CityName} />,
            ProgramName: <TableContent title="節目名稱" content={info.ProgramName} />,
            Summary: <TableContent title="簡介" content={info.Summary} />,
            Cost: <TableContent title="費用" content={Cost} />,
            AgeDesc: <TableContent title="年齡說明" content={info.AgeDesc} />,
            Remarks: <TableContent title="備註" content={info.Remarks} />,
            DangerLevel: <TableContent title="危險" content={info.DangerLevel} />,
        };
    });

    //行程特殊提醒
    const noticeContent =
        Remark?.length > 0 &&
        parse(sanitize(Remark, { allowedTags: false, allowedAttributes: false }));
    //行程備註
    const itineraryNote = ItinnotesReg?.length > 0 && parse(ItinnotesReg);
    //安全守則
    const safetyContent = SafeReg?.length > 0 && parse(SafeReg);
    //團體航班規定事項
    const leaveTeamContent = LeaveTeamReg?.length > 0 && parse(LeaveTeamReg);
    //活動附註
    const activeNoteContent = ActiveNote?.length > 0 && parse(ActiveNote);

    return (
        <div className={cN('itinerary_wrapper', 'Container')}>
            <ItinerarayTitle TourName={TourName} NormGroupID={NormGroupID} />
            <FeatureContent features={Features} />
            {MarketingContent?.ImgPC && <ItineraryMarketing MarketingContent={MarketingContent} />}
            {BankContent && (
                <div
                    className="itinerary_bankContent"
                    dangerouslySetInnerHTML={{
                        __html: sanitize(BankContent, {
                            allowedTags: false,
                            allowedAttributes: false,
                        }),
                    }}
                />
            )}
            {StationInfoList.length > 0 && (
                <div className={cN('itinerary_table', 'place')} id="assemble">
                    <SubTitle texts={{ main: '集合地點', sub: 'Assembling Place' }} />
                    <Table titles={placeTitles} dataSource={placeData} />
                    <div className="itinerary_note">
                        集合時間僅為參考用，實際時間請以當團人員通知時間為準
                    </div>
                </div>
            )}
            {DailyList.length > 0 && (
                <>
                    <div className="itinerary" id="schedules">
                        <SubTitle texts={{ main: '每日行程', sub: 'Daily Itinerary' }} />
                        <ItineraryDaily />
                    </div>
                    <ItineraryDailyLightbox />
                </>
            )}
            {OptionalInfoList.length > 0 && (
                <div className={cN('itinerary_table', 'activity')}>
                    <SubTitle texts={{ main: '自費活動', sub: 'Activities' }} />
                    <Table titles={activityTitles} dataSource={activityData} />
                    <div className="itinerary_note">
                        自費活動項目以實際出發日為主。
                        <span>危險等級： A危險 B一些危險 C安全</span>
                    </div>
                </div>
            )}
            {EachTripList.length > 0 && (
                <div className="itinerary_self">
                    <SubTitle texts={{ main: '產品內容', sub: 'Contents' }} />
                    <ItinerarySelfGuided />
                </div>
            )}
            {!!noticeContent && (
                <div className="itinerary_notice no_collapse" id="notes">
                    <SubTitle texts={{ main: '行程特殊提醒', sub: 'Itinerary Remarks' }} />
                    <div className="content">{noticeContent}</div>
                </div>
            )}
            {!!itineraryNote && (
                <div className="itinerary_notice" id="itineraryNote">
                    <ItineraryNoticesFrame
                        texts={{ main: '行程備註', sub: 'Itinerary Notes' }}
                        scrollEleId="itineraryNote"
                    >
                        {itineraryNote}
                    </ItineraryNoticesFrame>
                </div>
            )}

            {!!activeNoteContent && (
                <div className="itinerary_notice" id="activeNote">
                    <ItineraryNoticesFrame
                        texts={{ main: '活動附註', sub: 'Activity Notes' }}
                        scrollEleId="activeNote"
                    >
                        {activeNoteContent}
                    </ItineraryNoticesFrame>
                </div>
            )}
            {!!safetyContent && (
                <div className="itinerary_notice" id="safety">
                    <ItineraryNoticesFrame
                        texts={{ main: '安全守則', sub: 'Safety Rules' }}
                        scrollEleId="safety"
                    >
                        {safetyContent}
                    </ItineraryNoticesFrame>
                </div>
            )}
            {!!leaveTeamContent && (
                <div className="itinerary_notice" id="leaveTeam">
                    <ItineraryNoticesFrame
                        texts={{ main: '團體航班規定事項', sub: 'Group Flight Rules' }}
                        scrollEleId="leaveTeam"
                    >
                        {leaveTeamContent}
                    </ItineraryNoticesFrame>
                </div>
            )}
            {NoteList.length > 0 && (
                <div className="itinerary_infos" id="notices">
                    <SubTitle texts={{ main: '旅遊資訊', sub: 'Travel information' }} />
                    <div className="itinerary_infos_pc">
                        <ItineryTravelInfoPC />
                    </div>
                    <div className="itinerary_infos_m">
                        <ItineryTravelInfoM />
                    </div>
                </div>
            )}
        </div>
    );
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
    <React.StrictMode>
        <Provider store={store}>
            <Itineraray />
        </Provider>
    </React.StrictMode>
);
